import { Box, Button, Divider, Paper, Stack, TextField, Typography } from "@mui/material";
import { VIEW } from "../../../../utils/breakpoint";
import { useEffect, useState } from "react";
import { useCreateBuildingMutation } from "../../../../hooks/config-page";
import { CreateBuildingResponse } from "../../../../hooks/config-page/index.type";

type CreateBuildingFormProps = {
  onSuccess?: (buildingId: string) => void
}

type BuildingLevel = {
  level: number;
  level_name: string;
}

export default function CreateBuildingForm({ onSuccess }: CreateBuildingFormProps) {

  const { trigger } = useCreateBuildingMutation({
    onSuccess: (data: CreateBuildingResponse) => {
      onSuccess?.(data.data.building.building_uuid)
    }
  })
  const onSubmit = () => {
    const form = {
      name: buildingName,
      address: buildingAddress,
      levels: newLevelValue
    }

    trigger(form)

    // Need to change based on Backend flow
    // onSuccess?.('123981ac723828')
  }
  const [newLevelValue, setNewLevelValue] = useState<BuildingLevel[]>([]);
  const [buildingName, setBuildingName] = useState<string>('');
  const [buildingAddress, setBuildingAddress] = useState<string>('');

  const [buildingNameError, setBuildingNameError] = useState<string>('');
  const [buildingAddressError, setBuildingAddressError] = useState<string>('');

  const validateBuildingName = (name: string) => {
    if (!name || name.trim() === '') {
      setBuildingNameError('Building name is required');
      return false;
    }
    setBuildingNameError('');
    return true;
  }
  const validateBuildingAddress = (address: string) => {
    if (address.length > 2000) {
      setBuildingAddressError('Building address must be less than 2000 characters');
      return false;
    }
    setBuildingAddressError('');
    return true;
  }

  const isValid = buildingNameError === '' && buildingAddressError === '' && buildingName !== ''

  const handleAddLevel = () => {
    setNewLevelValue(prev => {
      const newLevels = [...prev];
      newLevels.push({
        level: newLevels.length + 1,
        level_name: `L${newLevels.length + 1}`
      });
      return newLevels;
    })
  }

  const handleChangeLevelName = (index: number, value: string) => {
    setNewLevelValue(prev => {
      const newLevels = [...prev];
      newLevels[index].level_name = value;
      return newLevels;
    });
  }

  const handleChangeLevelNumber = (index: number, value: number) => {
    setNewLevelValue(prev => {
      const newLevels = [...prev];
      newLevels[index].level = value;
      return newLevels;
    });
  }

  const handleRemoveLevel = (index: number) => {
    setNewLevelValue(prev => {
      const newLevels = [...prev];
      newLevels.splice(index, 1);
      return newLevels;
    });
  }

  const [levelNameError, setLevelNameError] = useState<string>('');

  const validateLevels = () => {
    setLevelNameError('')
    newLevelValue.forEach((level, index) => {
      if (!level.level_name || level.level_name.trim() === '') {
        setLevelNameError('E' + index + '| Level name is required');
      }
    });
  }

  const isLevelError = (index: number) => {
    if (levelNameError === '') return false;
    
    const parts = levelNameError.split('|');
    if (parts.length !== 2) return false;
    
    const errorIndex = parseInt(parts[0].replace('E', ''));
    return errorIndex === index;
  }

  return (
    <Paper elevation={2} sx={(theme) => ({
      padding: '24px',
      overflowX: 'hidden',
      [theme.breakpoints.up(VIEW.TABLET)]: {
        width: '100%'
      }
    })}>
      <Stack spacing={'16px'}>
        <Box sx={(theme) => ({
          display: 'flex',
          gap: '12px',
          [theme.breakpoints.down(VIEW.TABLET)]: {
            flexDirection: 'column'
          },
          [theme.breakpoints.up(VIEW.TABLET)]: {
            alignItems: 'center',
          }
        })}>
          <Typography sx={(theme) => ({
            [theme.breakpoints.up(VIEW.TABLET)]: {
              width: '280px',
            },
            flex: 'none'
          })}>
            Name:
          </Typography>
          <TextField
            size="small"
            sx={{ flex: 'auto' }}
            value={buildingName}
            onChange={(e) => setBuildingName(e.target.value)}
            onBlur={(e) => validateBuildingName(e.target.value)}
            error={!!buildingNameError}
            helperText={buildingNameError}
          />
        </Box>
        <Box sx={(theme) => ({
          display: 'flex',
          gap: '12px',
          [theme.breakpoints.down(VIEW.TABLET)]: {
            flexDirection: 'column'
          },
          [theme.breakpoints.up(VIEW.TABLET)]: {
            alignItems: 'center',
          }
        })}>
          <Typography sx={(theme) => ({
            [theme.breakpoints.up(VIEW.TABLET)]: {
              width: '280px',
            },
            flex: 'none'
          })}>
            Address (optional) :
          </Typography>
          <TextField
            size="small"
            sx={{ flex: 'auto' }}
            value={buildingAddress}
            onChange={(e) => setBuildingAddress(e.target.value)}
            onBlur={(e) => validateBuildingAddress(e.target.value)}
            error={!!buildingAddressError}
            helperText={buildingAddressError}
          />
        </Box>
        <Box sx={(theme) => ({
          display: 'flex',
          gap: '12px',
          [theme.breakpoints.down(VIEW.TABLET)]: {
            flexDirection: 'column'
          },
          [theme.breakpoints.up(VIEW.TABLET)]: {
            // alignItems: 'center',
          }
        })}>
          <Typography sx={(theme) => ({
            [theme.breakpoints.up(VIEW.TABLET)]: {
              width: '280px',
            },
            flex: 'none'
          })}>
            Number of Levels:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', flex: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '4px', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'column' }}>
                <Typography variant="body1">
                  {newLevelValue.length} Levels
                </Typography>
                {/* <Typography variant="caption" color="text.secondary">
              Levels can be added later
            </Typography> */}
              </Box>

              <Button size="small" variant="outlined" onClick={handleAddLevel}>Add Level</Button>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mt: '12px' }}>
              {newLevelValue.map((level, index) => (
                <Box key={index} sx={{ display: 'flex', gap: '12px', py: '8px', alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label={`Level Name`}
                    value={level.level_name}
                    onChange={(e) => handleChangeLevelName(index, e.target.value)}
                    onBlur={() => validateLevels()}
                    error={isLevelError(index)}
                    helperText={isLevelError(index) ? levelNameError.split('|')[1] : ''}
                    sx={{ minWidth: '100px', flex: '1', minHeight: '64px' }}
                  />
                  <TextField
                    size="small"
                    label="Level Number"
                    type="number"
                    value={level.level}
                    onChange={(e) => handleChangeLevelNumber(index, parseInt(e.target.value))}
                    sx={{ maxWidth: '200px', minHeight: '64px', flex: '1' }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'start',  minHeight: '64px' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      sx={{ height: '40px' }}
                      onClick={() => handleRemoveLevel(index)}
                    >Remove</Button>
                  </Box>
                </Box>
              ))}

              <span>
                {levelNameError}
              </span>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'end' }}>
          <Button
            disabled={!isValid}
            onClick={onSubmit}
            variant="contained"
          >
            Add Building
          </Button>
        </Box>
      </Stack>
    </Paper>
  )
}